/**
 * HTML template builder for the Kunden-Konformität export.
 *
 * renderKundenKonformitaet(data) → self-contained HTML string suitable for
 * an srcdoc iframe preview or window.print()-based PDF export.
 *
 * Uses the same CSS and layout conventions as produkt_konformitaet.ts.
 */

import {
  generateNegativeText,
  generatePositiveText,
  generateRegulatoryNotes,
} from "../../../components/conformity/conformityPdf"

const CANDIDATE_LIST_VERSION = "31. Aktualisierung"
const CANDIDATE_LIST_DATE = "2024-06-27"
const CANDIDATE_LIST_ENTRIES = 240

function esc(s: string | null | undefined): string {
  return (s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
}

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type KundenKonformitaetSvhcRow = {
  casNumber: string
  substanceName: string
  svhcGehalt: string
  locationInComponent: string
  componentName: string
}

export type KundenKonformitaetProduct = {
  productId: string
  productName: string
  rows: KundenKonformitaetSvhcRow[]
}

export type KundenKonformitaetHtmlData = {
  issuer: string
  issuerAddress: string
  creationDate: string
  signatory: string
  signatoryRole: string
  signatoryPlace: string
  signatoryDate: string
  customer: {
    customerId: string
    customerName: string
    isPositive: boolean
    totalProductCount: number
    svhcProductCount: number
    products: KundenKonformitaetProduct[]
  }
}

// ---------------------------------------------------------------------------
// SVHC table section
// ---------------------------------------------------------------------------

function renderSvhcSection(products: KundenKonformitaetProduct[]): string {
  const svhcProducts = products.filter((p) => p.rows.length > 0)
  if (svhcProducts.length === 0) return ""

  let rowIndex = 0
  const rows = svhcProducts
    .flatMap((p) =>
      p.rows.map((r) => {
        const tr = `
        <tr${rowIndex % 2 === 1 ? ' class="alt"' : ""}>
          <td>${esc(p.productName)}</td>
          <td>${esc(r.casNumber)}</td>
          <td>${esc(r.substanceName)}</td>
          <td>${esc(r.svhcGehalt)}</td>
          <td>${esc(r.locationInComponent)}</td>
        </tr>`
        rowIndex++
        return tr
      }),
    )
    .join("")

  return `
  <section>
    <h2>SVHC-Stofftabelle</h2>
    <table>
      <thead>
        <tr>
          <th>Produkt</th>
          <th>CAS-Nr.</th>
          <th>SVHC-Stoff</th>
          <th>Gehalt&nbsp;(%)</th>
          <th>Ort im Bauteil</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
  </section>`
}

// ---------------------------------------------------------------------------
// CSS — screen (iframe) + print
// ---------------------------------------------------------------------------

const CSS = `
  *, *::before, *::after { box-sizing: border-box; }

  body {
    margin: 0;
    padding: 16px 8px;
    background: #e4e4e7;
    font-family: Helvetica, Arial, sans-serif;
    font-size: 10pt;
    color: #1a1a1a;
  }

  .page {
    background: #fff;
    max-width: 760px;
    min-height: 520px;
    margin: 0 auto 16px;
    padding: 36px 44px 32px;
    box-shadow: 0 1px 6px rgba(0,0,0,.18);
    position: relative;
  }

  h1  { font-size: 17pt; margin: 0 0 3px; font-weight: bold; color: #111; }
  h2  { font-size: 10.5pt; font-weight: bold; color: #111; margin: 0 0 7px;
        padding-bottom: 4px; border-bottom: 1px solid #ececec; }
  p   { margin: 0; }
  hr  { border: none; border-top: 1px solid #e0e0e0; margin: 10px 0; }
  ul  { margin: 2px 0 0; padding-left: 16px; }
  li  { font-size: 9pt; line-height: 1.65; color: #333; }

  .subtitle    { font-size: 9pt; color: #777; }
  .declaration { font-size: 9pt; line-height: 1.6; color: #333; }

  .meta-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px 24px;
    margin: 10px 0;
  }
  .meta-label {
    font-size: 7pt; color: #999; margin: 0 0 1px;
    text-transform: uppercase; letter-spacing: .04em;
  }
  .meta-value { font-size: 9.5pt; font-weight: 600; color: #222; margin: 0 0 8px; }

  section { margin-bottom: 14px; }

  table { width: 100%; border-collapse: collapse; font-size: 8pt; margin-top: 2px; }
  thead tr { background: #2d2d32; }
  th { padding: 5px 7px; text-align: left; font-weight: 600; color: #fff; font-size: 7.5pt; }
  td { padding: 4px 7px; color: #333; vertical-align: top; border-bottom: 1px solid #f0f0f0; }
  tr.alt td { background: #f5f5f8; }

  .signature { margin-top: 24px; }
  .sig-line { width: 70mm; border-top: 1px solid #555; margin-bottom: 5px; }
  .sig-name { font-size: 10pt; font-weight: bold; margin: 0 0 2px; }
  .sig-role { font-size: 8pt; color: #888; margin: 0 0 2px; }

  .page-footer {
    display: flex;
    justify-content: space-between;
    font-size: 7pt;
    color: #c0c0c0;
    border-top: 1px solid #eee;
    padding-top: 5px;
    margin-top: 20px;
  }

  @media print {
    body { background: #fff; padding: 0; }
    .page {
      box-shadow: none;
      margin: 0;
      max-width: 100%;
      min-height: auto;
      padding: 14mm 18mm 12mm;
      break-after: page;
    }
    .page:last-child { break-after: avoid; }
  }
`

// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------

export function renderKundenKonformitaet(
  data: KundenKonformitaetHtmlData,
): string {
  const c = data.customer
  const declarationText = c.isPositive
    ? generatePositiveText(c.customerName, c.svhcProductCount)
    : generateNegativeText(c.customerName, c.totalProductCount)

  const svhcSection = c.isPositive ? renderSvhcSection(c.products) : ""

  const regulatoryItems = generateRegulatoryNotes()
    .split("\n")
    .filter((l) => l.trim())
    .map((l) => `<li>${esc(l)}</li>`)
    .join("")

  const body = `
  <div class="page">
    <header>
      <h1>Konformit&#228;tserkl&#228;rung REACH</h1>
      <p class="subtitle">gem&#228;&#223; Verordnung (EG) Nr.&#160;1907/2006 (REACH), Artikel&#160;33</p>
      <hr>
    </header>

    <div class="meta-grid">
      <div>
        <p class="meta-label">Aussteller</p>
        <p class="meta-value">${esc(data.issuer)}</p>
        <p class="meta-label">Adresse</p>
        <p class="meta-value">${esc(data.issuerAddress)}</p>
        <p class="meta-label">Erstellungsdatum</p>
        <p class="meta-value">${esc(data.creationDate)}</p>
      </div>
      <div>
        <p class="meta-label">Empf&#228;nger</p>
        <p class="meta-value">${esc(c.customerName)}</p>
        <p class="meta-label">Kundennummer</p>
        <p class="meta-value">${esc(c.customerId)}</p>
        <p class="meta-label">Kandidatenliste</p>
        <p class="meta-value">${esc(CANDIDATE_LIST_VERSION)} (${CANDIDATE_LIST_ENTRIES}&#160;Eintr&#228;ge,&#160;${esc(CANDIDATE_LIST_DATE)})</p>
      </div>
    </div>
    <hr>

    <section>
      <h2>Erkl&#228;rung</h2>
      <p class="declaration">${esc(declarationText)}</p>
    </section>

    ${svhcSection}

    <section>
      <h2>Regulatorische Hinweise</h2>
      <ul>${regulatoryItems}</ul>
    </section>

    <section class="signature">
      <div class="sig-line"></div>
      <p class="sig-name">${esc(data.signatory)}</p>
      <p class="sig-role">${esc(data.signatoryRole)}</p>
      <p class="sig-role">${esc(data.signatoryPlace)}, ${esc(data.signatoryDate)}</p>
    </section>

    <div class="page-footer">
      <span>turnus.ai QMS &#183; Vertraulich</span>
      <span>REACH-Konformit&#228;tserkl&#228;rung &#183; ${esc(c.customerName)} &#183; ${esc(data.creationDate)}</span>
    </div>
  </div>`

  return `<!DOCTYPE html>
<html lang="de">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>Konformit&#228;tserkl&#228;rung REACH &#8211; ${esc(c.customerName)}</title>
<style>${CSS}</style>
</head>
<body>${body}</body>
</html>`
}
